import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight } from "lucide-react";
import { IMAGES } from "@/data";

const ease = [0.76, 0, 0.24, 1];

const Line = ({ children, delay }) => (
  <span className="block overflow-hidden">
    <motion.span
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.2, delay, ease }}
      className="block"
    >
      {children}
    </motion.span>
  </span>
);

const scrollTo = (href) => document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const overlayY = useTransform(scrollYProgress, [0, 1], ["0%", "-12%"]);

  return (
    <section ref={ref} id="top" data-testid="hero" className="relative h-[100svh] w-full overflow-hidden grain">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={IMAGES.hero} alt="Everyday car being detailed in a wash bay" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-black/40" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-transparent" />
      </motion.div>

      <motion.div style={{ y: overlayY }} className="relative z-10 flex h-full flex-col justify-end px-6 pb-16 md:px-12 md:pb-20">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="mb-6 flex items-center gap-3 font-mono-x text-[11px] uppercase tracking-[0.3em] text-primary"
        >
          <span className="h-px w-10 bg-primary" />
          Fully Mobile · We Come To You
        </motion.div>

        <h1 className="font-cabinet text-[15vw] font-black uppercase leading-[0.82] tracking-tighter text-white md:text-[11vw] lg:text-[9.5vw]">
          <Line delay={0.5}><span className="gradient-text">Velocity</span></Line>
          <Line delay={0.62}>
            <span className="gradient-text">Detailing Co</span>
          </Line>
        </h1>

        <div className="mt-8 flex flex-col items-start justify-between gap-8 md:flex-row md:items-end">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.8 }}
            className="max-w-md text-base leading-relaxed text-zinc-300"
          >
            Concours-level detailing inside and out — 100% mobile, so we bring the full setup straight to your driveway.
          </motion.p>

          <motion.button
            data-testid="hero-cta"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.8 }}
            onClick={() => scrollTo("#booking")}
            className="group flex items-center gap-4 border border-white/20 bg-white/5 px-7 py-4 font-mono-x text-xs uppercase tracking-widest text-white backdrop-blur-sm transition-colors duration-300 hover:border-primary hover:bg-primary hover:text-primary-foreground"
          >
            Reserve A Slot
            <ArrowDownRight size={18} className="transition-transform duration-300 group-hover:rotate-45" />
          </motion.button>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="absolute right-6 top-1/2 z-10 hidden -translate-y-1/2 rotate-90 font-mono-x text-[10px] uppercase tracking-[0.4em] text-zinc-500 md:block"
      >
        Scroll to explore
      </motion.div>
    </section>
  );
};
